import { useState, useEffect } from 'react';
import { roomsApi, schoolsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, DoorOpen, Trash2, Edit, Lock, SprayCan, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export default function RoomsPage() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [schoolFilter, setSchoolFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    school_id: '',
    floor: '',
    building: '',
    room_type: '',
  });

  useEffect(() => {
    loadData();
  }, [schoolFilter]);

  const loadData = async () => {
    try {
      const [roomsRes, schoolsRes] = await Promise.all([
        roomsApi.list(schoolFilter !== 'all' ? schoolFilter : undefined),
        schoolsApi.list(),
      ]);
      setRooms(roomsRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      toast.error('Kunde inte ladda rum');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoom) {
        await roomsApi.update(editingRoom.id, formData);
        toast.success('Rummet uppdaterades');
      } else {
        await roomsApi.create(formData);
        toast.success('Rummet skapades');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Åtgärden misslyckades');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Är du säker på att du vill ta bort rummet? Alla kopplade sensorer tas också bort.')) return;
    try {
      await roomsApi.delete(id);
      toast.success('Rummet togs bort');
      loadData();
    } catch (error) {
      toast.error('Kunde inte ta bort rummet');
    }
  };

  const openEditDialog = (room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      school_id: room.school_id,
      floor: room.floor || '',
      building: room.building || '',
      room_type: room.room_type || '',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRoom(null);
    setFormData({ name: '', school_id: '', floor: '', building: '', room_type: '' });
  };

  const getSchoolName = (schoolId) => {
    return schools.find(s => s.id === schoolId)?.name || 'Okänd';
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar rum...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="rooms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Rum
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Hantera rum och enhetsplaceringar
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          {canCreate() ? (
            <DialogTrigger asChild>
              <Button className="bg-navy hover:bg-navy/90 rounded-sm font-ibm" data-testid="add-room-btn">
                <Plus className="h-4 w-4 mr-2" />
                Lägg till rum
              </Button>
            </DialogTrigger>
          ) : (
            <Button disabled className="rounded-sm font-ibm opacity-50" data-testid="add-room-btn-disabled">
              <Lock className="h-4 w-4 mr-2" />
              Lägg till rum
            </Button>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editingRoom ? 'Redigera rum' : 'Lägg till nytt rum'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Skola *</Label>
                <Select 
                  value={formData.school_id} 
                  onValueChange={(value) => setFormData({ ...formData, school_id: value })}
                  disabled={!!editingRoom}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="room-school-select">
                    <SelectValue placeholder="Välj en skola" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map((school) => (
                      <SelectItem key={school.id} value={school.id}>
                        {school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Rumsnamn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Pojktoalett – Plan 1"
                  required
                  className="bg-slate-50"
                  data-testid="room-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label className="font-ibm text-xs uppercase tracking-widest">Våning</Label>
                  <Input
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="1"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">Byggnad</Label>
                  <Input
                    value={formData.building}
                    onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                    placeholder="Huvudbyggnad"
                    className="bg-slate-50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Rumstyp</Label>
                <Select 
                  value={formData.room_type} 
                  onValueChange={(value) => setFormData({ ...formData, room_type: value })}
                >
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue placeholder="Välj typ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bathroom">Badrum/Toalett</SelectItem>
                    <SelectItem value="locker_room">Omklädningsrum</SelectItem>
                    <SelectItem value="hallway">Korridor</SelectItem>
                    <SelectItem value="gym">Gym</SelectItem>
                    <SelectItem value="classroom">Klassrum</SelectItem>
                    <SelectItem value="other">Övrigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-navy rounded-sm" data-testid="room-submit-btn">
                {editingRoom ? 'Uppdatera rum' : 'Skapa rum'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div>
        <Select value={schoolFilter} onValueChange={setSchoolFilter}>
          <SelectTrigger className="w-[200px] bg-white" data-testid="school-filter">
            <SelectValue placeholder="Filtrera på skola" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla skolor</SelectItem>
            {schools.map((school) => (
              <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rooms List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map((room) => (
          <Card key={room.id} className="border-slate-200" data-testid={`room-card-${room.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-slate-100 flex items-center justify-center">
                    <DoorOpen className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-ibm font-semibold text-slate-900">{room.name}</h3>
                    <p className="text-xs text-slate-500">{getSchoolName(room.school_id)}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-ibm">Enheter</span>
                  <span className="font-ibm font-semibold">{room.device_count}</span>
                </div>
                {room.floor && (
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-ibm">Våning</span>
                    <span className="font-ibm text-slate-700">{room.floor}</span>
                  </div>
                )}
                {room.room_type && (
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-ibm">Typ</span>
                    <span className="font-ibm text-slate-700 capitalize">{room.room_type.replace('_', ' ')}</span>
                  </div>
                )}
                {room.spray_count_30d > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-ibm flex items-center gap-1">
                      <SprayCan className="h-3 w-3" />
                      Spray (30 d)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-ibm font-semibold">{room.spray_count_30d}</span>
                      {room.spray_boost > 0 && (
                        <Badge
                          className={cn(
                            'text-[10px] font-ibm',
                            room.spray_boost >= 3
                              ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          )}
                          data-testid={`spray-boost-${room.id}`}
                        >
                          <ShieldAlert className="h-3 w-3 mr-0.5" />
                          +{room.spray_boost} spray-skydd
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(room)}
                  data-testid={`edit-room-${room.id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Redigera
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(room.id)}
                  data-testid={`delete-room-${room.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {rooms.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <DoorOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-slate-700">Inga rum ännu</h3>
            <p className="text-slate-500 mt-1 font-ibm">
              {schools.length === 0 
                ? 'Lägg till en skola först, skapa sedan rum'
                : 'Lägg till rum för att börja placera sensorer'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
